import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';

const experienceSteps = [
  {
    id: 1,
    title: 'Recepção',
    description: 'Ao chegar, você é recebido com café espresso, whisky ou cerveja artesanal. Um momento para desacelerar antes do atendimento.',
    icon: 'ri-cup-line',
    image: 'https://storage.helloreaddy.io/project_files/1fff4f2a-b372-4b40-8698-6f714b3b8587/9ee3f36a-85e3-49fd-92d0-9d70761223a6_compressed_Captura-de-tela-2026-07-30-171639.webp',
  },
  {
    id: 2,
    title: 'Atendimento',
    description: 'Nossos barbeiros analisam seu estilo, formato de rosto e preferências pessoais para criar o resultado perfeito para você.',
    icon: 'ri-user-voice-line',
    image: 'https://storage.helloreaddy.io/project_files/1fff4f2a-b372-4b40-8698-6f714b3b8587/f8ccb2b3-3d8c-4b0a-bcea-53ae7404f34e_compressed_Captura-de-tela-2026-07-30-183623.webp',
  },
  {
    id: 3,
    title: 'Ambiente',
    description: 'Música ambiente selecionada, iluminação quente, cadeiras de couro. Cada detalhe pensado para seu conforto e bem-estar.',
    icon: 'ri-building-2-line',
    image: 'https://readdy.ai/api/search-image?query=Luxury%20barbershop%20interior%20wide%20shot%20with%20leather%20barber%20chairs%2C%20dark%20wood%20paneling%2C%20warm%20moody%20lighting%2C%20vintage%20decor%2C%20masculine%20design%2C%20cinematic%20photography%2C%20atmospheric%20and%20inviting&width=1200&height=800&seq=exp-ambiente-03&orientation=landscape',
  },
  {
    id: 4,
    title: 'Produtos',
    description: 'Utilizamos exclusivamente produtos premium importados. Cada produto é selecionado para oferecer o melhor cuidado para seu cabelo e barba.',
    icon: 'ri-flask-line',
    image: 'https://readdy.ai/api/search-image?query=Premium%20men%20grooming%20products%20displayed%20on%20dark%20wood%20shelf%2C%20luxury%20hair%20and%20beard%20products%2C%20warm%20lighting%2C%20masculine%20aesthetic%2C%20editorial%20still%20life%2C%20charcoal%20tones&width=1200&height=800&seq=exp-produtos-04&orientation=landscape',
  },
  {
    id: 5,
    title: 'Detalhes',
    description: 'O acabamento faz toda a diferença. Navalha, toalha quente, massagem capilar. O ritual completo do homem moderno.',
    icon: 'ri-magic-line',
    image: 'https://readdy.ai/api/search-image?query=Close%20up%20of%20barber%20finishing%20haircut%20with%20precision%20detail%20work%2C%20using%20straight%20razor%2C%20dark%20moody%20lighting%2C%20masculine%20aesthetic%2C%20cinematic%20photography%2C%20warm%20amber%20highlights&width=1200&height=800&seq=exp-detalhes-05&orientation=landscape',
  },
  {
    id: 6,
    title: 'Conforto',
    description: 'Toalhas aquecidas, bebidas premium, cadeiras reclináveis. Sua pausa merecida do mundo lá fora, no conforto que você merece.',
    icon: 'ri-heart-line',
    image: 'https://readdy.ai/api/search-image?query=Premium%20barbershop%20client%20relaxing%20in%20leather%20chair%20with%20hot%20towel%2C%20dark%20comfortable%20atmosphere%2C%20warm%20lighting%2C%20masculine%20self%20care%2C%20cinematic%20photography%2C%20steam%20visible&width=1200&height=800&seq=exp-conforto-06&orientation=landscape',
  },
];

export default function ExperienceSection() {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <section id="experiencia" className="relative py-24 md:py-32 lg:py-40 bg-dark-950 overflow-hidden">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
        <div ref={ref} className="text-center mb-16 md:mb-20">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-premium-400 text-xs tracking-[0.3em] uppercase font-medium"
          >
            A Experiência
          </motion.span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mt-4">
            Cada momento é{' '}
            <span className="text-gradient-gold">pensado para você</span>
          </h2>
          <motion.div
            initial={{ width: 0 }}
            animate={isInView ? { width: '80px' } : {}}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="h-[1px] bg-premium-500 mx-auto mt-6"
          ></motion.div>
        </div>

        <div className="flex overflow-x-auto gap-6 pb-4 no-scrollbar snap-x snap-mandatory">
          {experienceSteps.map((step, index) => (
            <motion.div
              key={step.id}
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.12, ease: [0.4, 0, 0.2, 1] }}
              className="flex-shrink-0 w-[85vw] md:w-[500px] lg:w-[550px] snap-center group"
            >
              <div className="relative h-[350px] md:h-[400px] rounded-lg overflow-hidden mb-6">
                <img
                  src={step.image}
                  alt={step.title}
                  className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-dark-950 via-dark-950/30 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-md bg-premium-600/30 flex items-center justify-center">
                      <i className={`${step.icon} text-premium-400 text-base`}></i>
                    </div>
                    <span className="text-premium-400 text-xs tracking-[0.2em] uppercase font-medium">
                      Etapa {step.id}
                    </span>
                  </div>
                  <h3 className="font-heading text-2xl md:text-3xl font-bold text-white mb-2">
                    {step.title}
                  </h3>
                  <p className="text-white/60 text-sm leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}